import {
  CUSTOMER_WORKSPACE_ROLES,
  PLATFORM_ACCOUNT_ROLES,
  type CanonicalAccountRole,
  type PlatformRole,
  type UserRole,
  type WorkspaceRole,
  type MvpAccountRole,
  type OrganizationAccountRole,
} from '@/types';
import { isMvpAccountRole } from '@/types';

const TOKEN_OPS = 'business_admin';
const TOKEN_WS = 'workspace_admin';

export { CUSTOMER_WORKSPACE_ROLES, PLATFORM_ACCOUNT_ROLES };

export type LegacyRoleClassification =
  | 'canonical'
  | 'scoped_relationship_required'
  | 'capability_bundle_required'
  | 'deprecated_classification_only'
  | 'ambiguous_quarantined'
  | 'orphaned_denied';

const LEGACY_ROLE_CLASSIFICATION: Readonly<Record<string, LegacyRoleClassification>> = {
  system_admin: 'capability_bundle_required',
  security_admin: 'capability_bundle_required',
  commercial_admin: 'capability_bundle_required',
  implementation_admin: 'capability_bundle_required',
  support_admin: 'capability_bundle_required',
  workspace_admin: 'scoped_relationship_required',
  business_admin: 'scoped_relationship_required',
  customer_security_admin: 'capability_bundle_required',
  project_manager: 'scoped_relationship_required',
  partner: 'scoped_relationship_required',
  vendor: 'scoped_relationship_required',
  investor: 'scoped_relationship_required',
  // 'client' removed - use organization_member + company_representative instead
  company: 'deprecated_classification_only',
};

export function resolveMvpAccountRole(role: string | null | undefined): MvpAccountRole | null {
  if (!role) return null;
  const normalized = normalizeRoleToken(role);
  return isMvpAccountRole(normalized) ? normalized : null;
}

/**
 * Product display labels for canonical account roles.
 */
export const MVP_ACCOUNT_ROLE_LABELS: Readonly<Record<MvpAccountRole, string>> = {
  organization_owner: 'Organisation Owner',
  organization_admin: 'Workspace Admin',
  organization_member: 'Workspace Member',
  platform_owner: 'Platform Owner',
  platform_admin: 'Platform Admin',
  support_operator: 'Support Operator',
};

export function resolveUserMvpAccountRole(
  user: { account_role?: string | null } | null | undefined,
): MvpAccountRole | null {
  return resolveMvpAccountRole(user?.account_role);
}

export function getMvpAccountRoleLabel(role: MvpAccountRole): string {
  return MVP_ACCOUNT_ROLE_LABELS[role];
}

export function classifyLegacyAccountRole(
  role: string | null | undefined,
): LegacyRoleClassification {
  if (!role) return 'orphaned_denied';
  const normalized = normalizeRoleToken(role);
  if (isMvpAccountRole(normalized)) return 'canonical';
  return LEGACY_ROLE_CLASSIFICATION[normalized] ?? 'orphaned_denied';
}

export function canAdministerMvpAccountRole(
  actorRole: MvpAccountRole | null | undefined,
  targetRole: MvpAccountRole | null | undefined,
): boolean {
  if (!actorRole || !targetRole) return false;
  if (targetRole === 'platform_owner' || targetRole === 'platform_admin' || targetRole === 'support_operator') {
    return actorRole === 'platform_owner';
  }
  const customerActor: OrganizationAccountRole | null =
    actorRole === 'organization_owner' || actorRole === 'organization_admin' ? actorRole : null;
  if (!customerActor) return false;
  if (targetRole === 'organization_owner') return customerActor === 'organization_owner';
  return targetRole === 'organization_admin' || targetRole === 'organization_member';
}

const RETIRED_PLATFORM_ROLE: PlatformRole = 'platform_admin';
export type ActivePlatformRole = Exclude<PlatformRole, 'platform_admin'>;
export type ActiveCanonicalAccountRole = Exclude<CanonicalAccountRole, 'platform_admin'>;

/**
 * @deprecated MIGRATION ONLY
 * Legacy platform role mappings for reading old records.
 * Do NOT use for new role assignments.
 * These scoped relationships should use authority.ts ScopedRelationshipType instead.
 */
export const LEGACY_PLATFORM_ROLE_MIGRATION: Readonly<Record<string, ActivePlatformRole>> = {
  platform_admin: 'support_admin',
  admin: 'support_admin',
  administrator: 'support_admin',
  owner: 'platform_owner',
  super_admin: 'platform_owner',
};

/**
 * @deprecated MIGRATION ONLY
 * Legacy workspace role mappings for reading old records.
 * Do NOT use for new role assignments.
 * These scoped relationships should use authority.ts ScopedRelationshipType instead.
 * 
 * Note: scoped supplier, ally, capital, and delivery tokens are NOT account memberships.
 * They are scoped relationships that should only appear in ScopedRelationshipAuthority.
 */
export const LEGACY_WORKSPACE_ROLE_MIGRATION: Readonly<Record<string, WorkspaceRole>> = {
  admin: 'organization_admin',
  administrator: 'organization_admin',
  owner: 'organization_admin',
  // member, team_member, business_dev_manager, contractor do NOT map automatically
  // These require evidence-based migration to scoped relationships
};

/**
 * Context-free compatibility mapping.
 *
 * @deprecated MIGRATION ONLY
 * Ambiguous legacy roles are interpreted in the customer plane only for historical reasons.
 * Call normalizePlatformRole() only when the value came from platform_role field.
 */
export const LEGACY_ROLE_MIGRATION: Readonly<Record<string, ActiveCanonicalAccountRole>> =
  LEGACY_WORKSPACE_ROLE_MIGRATION;

function normalizeRoleToken(role: string): string {
  return role.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export function isPlatformRole(role: string | null | undefined): role is PlatformRole {
  return Boolean(role && PLATFORM_ACCOUNT_ROLES.includes(role as PlatformRole));
}

export function isWorkspaceRole(role: string | null | undefined): role is WorkspaceRole {
  return Boolean(role && CUSTOMER_WORKSPACE_ROLES.includes(role as WorkspaceRole));
}

export function normalizeAccountRole(
  role: string | null | undefined,
): ActiveCanonicalAccountRole | null {
  if (!role) return null; // Fail closed - do not default to supplier token
  const normalized = normalizeRoleToken(role);

  // The retired broad role is accepted only as migration input and is always
  // reduced to the least-privileged named platform function.
  if (normalized === RETIRED_PLATFORM_ROLE) return 'support_admin';
  if (isPlatformRole(normalized)) return normalized as ActivePlatformRole;
  if (isWorkspaceRole(normalized)) return normalized;
  return LEGACY_ROLE_MIGRATION[normalized] ?? null; // Fail closed for unknown roles
}

export function normalizePlatformRole(
  role: string | null | undefined,
): ActivePlatformRole | null {
  if (!role) return null; // Fail closed
  const normalized = normalizeRoleToken(role);
  if (normalized !== RETIRED_PLATFORM_ROLE && isPlatformRole(normalized)) {
    return normalized as ActivePlatformRole;
  }
  return LEGACY_PLATFORM_ROLE_MIGRATION[normalized] ?? null; // Fail closed for unknown roles
}

export function normalizeWorkspaceRole(
  role: string | null | undefined,
): WorkspaceRole | null {
  if (!role) return null; // Fail closed
  const normalized = normalizeRoleToken(role);
  if (isWorkspaceRole(normalized)) return normalized;
  return LEGACY_WORKSPACE_ROLE_MIGRATION[normalized] ?? null; // Fail closed for unknown roles
}

export function isLegacyUserRole(role: UserRole): boolean {
  return role === RETIRED_PLATFORM_ROLE || (!isPlatformRole(role) && !isWorkspaceRole(role));
}

/**
 * Compatibility guard for account-directory actions.
 *
 * This deliberately avoids a single cross-plane "power level": an AX1
 * control-plane role is not a superior customer-workspace membership, and a
 * customer administrator can never administer an AX1 staff account.
 * Resource scope, workspace membership and server policy remain authoritative.
 */
export function canAdministerAccountRole(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = normalizeAccountRole(actorRole);
  const target = normalizeAccountRole(targetRole);

  // Unknown/null roles cannot administer
  if (!actor || !target) return false;

  // Platform authority
  if (isPlatformRole(target)) {
    return actor === 'platform_owner';
  }

  // Platform roles cannot administer customer roles
  if (isPlatformRole(actor)) {
    return false;
  }

  // Organization admin may manage workspace-level admins
  if (actor === 'organization_admin') {
    return isWorkspaceRole(target) && target !== 'organization_admin';
  }

  // Ops membership may manage workspace-level admins (but not organization_admin)
  if (actor === TOKEN_OPS) {
    return target === TOKEN_WS;
  }

  // Workspace-level admin cannot manage other memberships
  // Scoped relationships are managed separately via ScopedRelationshipAuthority
  return false;
}
